import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Languages, Check, Mail, Phone, MessageCircle, MessageSquare } from "lucide-react";
import { Seo } from "@/components/layout/Seo";
import { Reveal } from "@/components/Reveal";
import { FinalCta } from "@/components/sections/CallToActions";
import { useLang } from "@/context/LanguageContext";
import { getActiveTeam } from "@/data/team";

const channelHref = (c) => {
  const digits = (c.number || "").replace(/[^\d]/g, "");
  if (c.type === "whatsapp") return `https://wa.me/${digits}`;
  if (c.type === "sms") return `sms:+${digits}`;
  if (c.type === "tel") return `tel:+${digits}`;
  return `mailto:${c.email || ""}`;
};

const ChannelIcon = ({ type }) => {
  const Icon = { whatsapp: MessageCircle, sms: MessageSquare, tel: Phone, mailto: Mail }[type] || MessageCircle;
  return <Icon size={15} strokeWidth={1.5} aria-hidden="true" />;
};


export default function TeamMemberPage() {
  const { lang, t } = useLang();
  const { memberId } = useParams();
  const member = getActiveTeam().find((m) => m.id === memberId);
  if (!member) return <Navigate to={`/${lang}/team`} replace />;
  const firstName = member.name.split(" ")[0];
  const fill = (s) => s.replace("{name}", firstName);
  const callIn = (place) => fill(t.teamPage.callIn).replace("{place}", place ? place[lang] : "");
  const primaryCallLabel = member.phone2 && member.phonePlace ? callIn(member.phonePlace) : fill(t.teamPage.callDirect);
  const paragraphs = member.profile ? member.profile[lang] : [member.bio[lang]];
  return (
    <>
      <Seo page="team" title={`${member.name} | ${t.brandName}`} description={member.bio[lang]} />
      <section className="pt-10 md:pt-14 pb-16 md:pb-24" data-testid="team-member-page">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link
            to={`/${lang}/team`}
            data-testid="member-back-link"
            className="group inline-flex items-center gap-2 text-sm font-medium text-ser-purple hover:text-navy transition-colors"
          >
            <ArrowLeft size={16} strokeWidth={1.5} className="transition-transform group-hover:-translate-x-1" />
            {t.teamPage.backToTeam}
          </Link>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <Reveal className="lg:col-span-5">
              <div className="relative overflow-hidden aspect-[4/5] bg-pale-violet" data-testid="member-photo">
                <img src={member.photo} alt={member.name} className="h-full w-full object-cover" />
                <span className="absolute left-0 bottom-0 h-1 w-full bg-ser-yellow" aria-hidden="true" />
              </div>
            </Reveal>
            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <p className="text-xs uppercase tracking-[0.25em] font-medium text-ser-purple">{t.teamPage.overline}</p>
                <h1 className="mt-3 font-display text-4xl md:text-5xl leading-[1.08] tracking-tight text-navy">{member.name}</h1>
                <p className="mt-3 text-sm uppercase tracking-[0.18em] text-ser-purple font-medium">{member.role[lang]}</p>
                <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-navy/60">
                  {member.country && (
                    <span className="flex items-center gap-1.5">
                      <MapPin size={14} strokeWidth={1.5} aria-hidden="true" />
                      {member.country[lang]}
                    </span>
                  )}
                  {member.languages && (
                    <span className="flex items-center gap-1.5">
                      <Languages size={14} strokeWidth={1.5} aria-hidden="true" />
                      {member.languages[lang]}
                    </span>
                  )}
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="mt-8 space-y-5 text-base leading-relaxed text-navy/75" data-testid="member-profile">
                  {paragraphs.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </Reveal>
              {member.specialties && (
                <Reveal delay={0.3}>
                  <h2 className="mt-10 text-xs uppercase tracking-[0.2em] text-navy/50 font-medium">
                    {t.teamPage.specialtiesTitle}
                  </h2>
                  <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3" data-testid="member-specialties">
                    {member.specialties[lang].map((s) => (
                      <li key={s} className="flex items-start gap-2.5 text-sm leading-snug text-navy/80">
                        <Check size={15} strokeWidth={2} className="mt-0.5 shrink-0 text-ser-yellow" aria-hidden="true" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              )}
              <Reveal delay={0.4}>
                <div className="mt-10 flex flex-wrap gap-4">
                  {member.channels &&
                    member.channels.map((c, i) => (
                      <a
                        key={`${c.type}-${i}`}
                        href={channelHref(c)}
                        target={c.type === "whatsapp" ? "_blank" : undefined}
                        rel={c.type === "whatsapp" ? "noopener noreferrer" : undefined}
                        data-testid={`member-channel-${c.type}-${i}`}
                        className="inline-flex items-center gap-2 border border-navy/20 text-navy text-sm font-medium px-6 py-3 hover:border-ser-purple hover:text-ser-purple transition-colors"
                      >
                        <ChannelIcon type={c.type} />
                        {c.label[lang]}
                      </a>
                    ))}
                  {!member.channels && member.email && (
                    <a
                      href={`mailto:${member.email}`}
                      data-testid="member-contact-direct-button"
                      className="inline-flex items-center gap-2 border border-navy/20 text-navy text-sm font-medium px-6 py-3 hover:border-ser-purple hover:text-ser-purple transition-colors"
                    >
                      <Mail size={15} strokeWidth={1.5} aria-hidden="true" />
                      {fill(t.teamPage.contactDirect)}
                    </a>
                  )}
                  {!member.channels && member.phone && (
                    <>
                      <a
                        href={`tel:${member.phone.replace(/\s/g, "")}`}
                        data-testid="member-call-button"
                        className="inline-flex items-center gap-2 border border-navy/20 text-navy text-sm font-medium px-6 py-3 hover:border-ser-purple hover:text-ser-purple transition-colors"
                      >
                        <Phone size={15} strokeWidth={1.5} aria-hidden="true" />
                        {primaryCallLabel}
                      </a>
                      {member.phone2 && (
                        <a
                          href={`tel:${member.phone2.replace(/\s/g, "")}`}
                          data-testid="member-call-alt-button"
                          className="inline-flex items-center gap-2 border border-navy/20 text-navy text-sm font-medium px-6 py-3 hover:border-ser-purple hover:text-ser-purple transition-colors"
                        >
                          <Phone size={15} strokeWidth={1.5} aria-hidden="true" />
                          {member.phone2Place ? callIn(member.phone2Place) : member.phone2}
                        </a>
                      )}
                      <a
                        href={`https://wa.me/${member.phone.replace(/[^\d]/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="member-whatsapp-button"
                        className="inline-flex items-center gap-2 border border-navy/20 text-navy text-sm font-medium px-6 py-3 hover:border-ser-purple hover:text-ser-purple transition-colors"
                      >
                        <MessageCircle size={15} strokeWidth={1.5} aria-hidden="true" />
                        {fill(t.teamPage.whatsappDirect)}
                      </a>
                    </>
                  )}
                  <Link
                    to={`/${lang}/contact`}
                    data-testid="member-hire-button"
                    className="bg-navy text-white text-sm font-medium px-6 py-3 hover:bg-ser-purple transition-colors"
                  >
                    {fill(t.teamPage.hireVia)}
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
      <FinalCta />
    </>
  );
}
